import { Access, AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import {
  PATH_ADD,
  PATH_ASSETS,
  PATH_CATEGORIES,
  PATH_COUNTERS,
  PATH_DASHBOARD,
  PATH_DATA_IMPORT,
  PATH_EDIT,
  PATH_FORM_CHECKLISTS,
  PATH_FORM_TEMPLATES,
  PATH_FORM_TEMPLATES_ACTIVE,
  PATH_FORM_TEMPLATES_ARCHIVED,
  PATH_INTERVENTIONS,
  PATH_PERFORM_TASKS,
  PATH_SECTORS,
  PATH_SITES,
  PATH_TASKS,
  PATH_TEAMS,
  PATH_USERS,
  PATH_HOME,
  PATH_WIDGETS,
  PATH_CUSTOMIZE,
} from '@lib/enums/urls';

function matchPath(pathTemplate: string, actualPath: string): boolean {
  const normalizedPathname = actualPath.startsWith('/')
    ? actualPath.slice(1)
    : actualPath;
  const regex = new RegExp(`^${pathTemplate.replace(/:[^/]+/g, '[^/]+')}$`);
  return (
    normalizedPathname.startsWith(pathTemplate) ||
    regex.test(normalizedPathname)
  );
}

const ACCESSES_MAP = [
  {
    path: `${PATH_HOME}/${PATH_CUSTOMIZE}`,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: `${PATH_HOME}/${PATH_WIDGETS}`,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: `${PATH_HOME}/${PATH_WIDGETS}/${PATH_ADD}`,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: PATH_HOME,
    component: AccessComponentEnum.HomePageManagement,
  },
  {
    path: `${PATH_INTERVENTIONS}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.InterventionCategoriesManagement,
  },
  {
    path: PATH_INTERVENTIONS,
    component: AccessComponentEnum.InterventionsManagement,
  },
  {
    path: `${PATH_TASKS}/:ID/${PATH_INTERVENTIONS}`,
    component: AccessComponentEnum.InterventionsManagement,
  },
  {
    path: `${PATH_ASSETS}/:ID/${PATH_INTERVENTIONS}`,
    component: AccessComponentEnum.InterventionsManagement,
  },
  {
    path: PATH_PERFORM_TASKS,
    component: AccessComponentEnum.TasksPerforming,
  },
  {
    path: PATH_TASKS,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: `${PATH_FORM_TEMPLATES}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.FormCategoriesManagement,
  },
  {
    path: PATH_FORM_TEMPLATES,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_TEMPLATES_ACTIVE,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_TEMPLATES_ARCHIVED,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_CHECKLISTS,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: `${PATH_SITES}/:ID/${PATH_USERS}`,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: PATH_SITES,
    component: AccessComponentEnum.SitesManagement,
  },
  {
    path: `${PATH_SECTORS}/:ID/${PATH_USERS}`,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: PATH_SECTORS,
    component: AccessComponentEnum.SitesManagement,
  },
  {
    path: PATH_USERS,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: PATH_TEAMS,
    component: AccessComponentEnum.UsersManagement,
  },
  {
    path: `${PATH_ASSETS}/${PATH_CATEGORIES}`,
    component: AccessComponentEnum.AssetCategoriesManagement,
  },
  {
    path: `${PATH_ASSETS}/${PATH_COUNTERS}`,
    component: AccessComponentEnum.AssetsCounterReading,
  },
  {
    path: PATH_ASSETS,
    component: AccessComponentEnum.AssetsManagement,
  },
  {
    path: PATH_DASHBOARD,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_FORM_TEMPLATES,
    component: AccessComponentEnum.TasksManagement,
  },
  {
    path: PATH_DATA_IMPORT,
    component: [
      AccessComponentEnum.SitesManagement,
      AccessComponentEnum.AssetsManagement,
    ],
  },
];

export function checkUserAccessForPage(
  accesses: Access[] | undefined,
  pathname: string,
) {
  if (!pathname || !accesses?.length) return undefined;

  const matchedRoute = ACCESSES_MAP.find(({ path }) =>
    matchPath(path, pathname),
  );

  if (!matchedRoute) return undefined;

  const { component } = matchedRoute;

  if (Array.isArray(component)) {
    const relevantAccesses = accesses.filter(({ component: userComponent }) =>
      component.includes(userComponent),
    );

    if (!relevantAccesses.length) return undefined;

    // LimitedRead < Read < Write
    const accessLevelsOrder = [
      AccessLevelEnum.LimitedRead,
      AccessLevelEnum.Read,
      AccessLevelEnum.Write,
    ];

    const maxLevel = relevantAccesses.reduce((max, curr) => {
      const currLevel = curr.level ?? AccessLevelEnum.LimitedRead;
      return accessLevelsOrder.indexOf(currLevel) >
        accessLevelsOrder.indexOf(max)
        ? currLevel
        : max;
    }, AccessLevelEnum.LimitedRead);

    const isEditingOrAdding = [PATH_ADD, PATH_EDIT].some((subPath) =>
      pathname.includes(`/${subPath}`),
    );

    if (isEditingOrAdding && maxLevel === AccessLevelEnum.Read) {
      return AccessLevelEnum.LimitedRead;
    }

    return maxLevel;
  }

  const access = accesses.find(
    ({ component: userComponent }) => userComponent === component,
  );

  if (!access) return undefined;

  const { level } = access;

  // Check for PATH_ADD or PATH_EDIT in the pathname
  const isEditingOrAdding = [PATH_ADD, PATH_EDIT].some((subPath) =>
    pathname.includes(`/${subPath}`),
  );

  if (isEditingOrAdding && level === AccessLevelEnum.Read) {
    return AccessLevelEnum.LimitedRead;
  }

  return level || AccessLevelEnum.LimitedRead;
}

export function checkUserAccessByComponent(
  accesses: Access[] | undefined,
  findBy: AccessComponentEnum,
) {
  if (!findBy || !accesses?.length) return undefined;
  const access = accesses.find(({ component }) => component === findBy);
  if (access?.level) return access.level;
  return AccessLevelEnum.LimitedRead;
}

export function checkUserAccessItemIdsByComponent(
  accesses: Access[] | undefined,
  findBy: AccessComponentEnum,
) {
  if (!findBy || !accesses?.length) return undefined;
  const access = accesses.find(({ component }) => component === findBy);
  return access?.itemIds;
}
